import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaArrowLeft } from 'react-icons/fa';
import { useTranslation } from '../../utils/translations';
import LanguageToggle from '../Common/LanguageToggle';
import './VerificationCode.css';
import { isUsingMockData } from '../../services/apiService';

const VerificationCode = ({ phoneNumber, verificationMethod, onSubmit, onBack, error, isLoading }) => {
  const [code, setCode] = useState(Array(6).fill(''));
  const [localError, setLocalError] = useState('');
  const inputsRef = useRef([]);
  const { t } = useTranslation();

  // The phoneNumber is already formatted like "(123) 456-7890"
  const formattedPhone = phoneNumber;

  // Get the method display text
  const getMethodText = () => {
    if (verificationMethod === 'sms') {
      return t('auth.textMethod');
    } else if (verificationMethod === 'call') {
      return t('auth.callMethod');
    }
    return 'verification code';
  };

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (/^[0-9]$/.test(value) || value === '') {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      setLocalError('');

      // Move to next input
      if (value !== '' && index < 5) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // Move to previous input on backspace
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '');
    if (pasteData.length === 6) {
      const newCode = pasteData.split('');
      setCode(newCode);
      inputsRef.current[5].focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    
    if (fullCode.length !== 6) {
      setLocalError(t('auth.enterFullCode'));
      return;
    }
    
    if (!isLoading) {
      onSubmit(fullCode);
    }
  };

  return (
    <div className="verification-page">
      <div className="verification-container">
        <div className="verification-top-controls">
          <button onClick={onBack} className="back-button">
            <FaArrowLeft />
          </button>
          <LanguageToggle />
        </div>
        
        <div className="verification-header">
          <img src="/logo.svg" alt="AoS Logo" className="verification-logo" />
          <h1 className="verification-title">{t('auth.verificationTitle')}</h1>
          <p className="verification-subtitle">
            {t('auth.verificationSubtitle')}<br />
            <strong className="phone-number-golden">{formattedPhone}</strong>
          </p>
          <p className="verification-method">
            via {getMethodText()}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="verification-form">
          <div className="form-group">
            <label className="form-label">{t('auth.codeLabel')}</label>
            <div className="code-inputs" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="tel"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="code-input"
                  aria-label={`Digit ${index + 1} of verification code`}
                />
              ))}
            </div>
            {(error || localError) && <p className="error-text">{error || localError}</p>}
          </div>
          
          <button 
            type="submit" 
            className="verification-button"
            disabled={code.join('').length !== 6 || isLoading}
          >
            {isLoading ? 'Verifying...' : t('auth.verifyButton')}
          </button>
        </form>
      </div>
    </div>
  );
};

VerificationCode.propTypes = {
  phoneNumber: PropTypes.string.isRequired,
  verificationMethod: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default VerificationCode;

